import { useEffect, useMemo, useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MaintenanceOptionalSelect } from '@/products/maintenance/features/maintenance-filter-select';
import {
  useMaintenanceCustomer,
  useMaintenanceCustomers,
  useMaintenanceInstallation,
  useMaintenanceInstallations,
  useMaintenanceProject,
  useMaintenanceProjects,
} from '@/products/maintenance/features/use-maintenance';

const SEARCH_DEBOUNCE_MS = 300;

function useDebouncedQuery() {
  const [draft, setDraft] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => setQ(draft.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [draft]);

  function flush() {
    setQ(draft.trim());
  }

  return { draft, setDraft, q, flush };
}

function mergeSelectedOption(
  items: { value: string; label: string }[],
  value: string,
  selectedLabel: string | null,
): { value: string; label: string }[] {
  if (!value || !selectedLabel) {
    return items;
  }
  if (items.some((item) => item.value === value)) {
    return items;
  }
  return [{ value, label: selectedLabel }, ...items];
}

export function MaintenanceCustomerSearchFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (customerId: string) => void;
}) {
  const search = useDebouncedQuery();
  const list = useMaintenanceCustomers({
    page: 1,
    pageSize: 25,
    sortBy: 'name',
    sortOrder: 'asc',
    q: search.q || undefined,
  });
  const selected = useMaintenanceCustomer(value);
  const options = useMemo(
    () =>
      mergeSelectedOption(
        (list.data?.items ?? []).map((row) => ({ value: row.id, label: row.name })),
        value,
        selected.data?.name ?? null,
      ),
    [list.data?.items, selected.data?.name, value],
  );

  return (
    <div className="min-w-0 space-y-2">
      <Label htmlFor="filter-customer-q">Kunde suchen</Label>
      <Input
        id="filter-customer-q"
        className="min-h-11"
        placeholder="Name oder Nummer"
        value={search.draft}
        onChange={(e) => search.setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            search.flush();
          }
        }}
      />
      <MaintenanceOptionalSelect
        label="Kunde"
        id="filter-customer"
        value={value}
        onValueChange={onChange}
        items={options}
        disabled={list.isPending}
      />
    </div>
  );
}

export function MaintenanceProjectSearchFilter({
  customerId,
  value,
  onChange,
  disabled,
}: {
  customerId?: string;
  value: string;
  onChange: (projectId: string) => void;
  disabled?: boolean;
}) {
  const search = useDebouncedQuery();
  const list = useMaintenanceProjects({
    page: 1,
    pageSize: 25,
    sortBy: 'name',
    sortOrder: 'asc',
    customerId,
    q: search.q || undefined,
  });
  const selected = useMaintenanceProject(value);
  const options = useMemo(
    () =>
      mergeSelectedOption(
        (list.data?.items ?? []).map((row) => ({ value: row.id, label: row.name })),
        value,
        selected.data?.name ?? null,
      ),
    [list.data?.items, selected.data?.name, value],
  );

  return (
    <div className="min-w-0 space-y-2">
      <Label htmlFor="filter-project-q">Projekt suchen</Label>
      <Input
        id="filter-project-q"
        className="min-h-11"
        placeholder="Name oder Nummer"
        value={search.draft}
        disabled={disabled}
        onChange={(e) => search.setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            search.flush();
          }
        }}
      />
      <MaintenanceOptionalSelect
        label="Projekt"
        id="filter-project"
        value={value}
        onValueChange={onChange}
        items={options}
        disabled={(disabled ?? false) || list.isPending}
      />
    </div>
  );
}

export function MaintenanceInstallationSearchFilter({
  customerId,
  projectId,
  value,
  onChange,
  disabled,
}: {
  customerId?: string;
  projectId?: string;
  value: string;
  onChange: (installationId: string) => void;
  disabled?: boolean;
}) {
  const search = useDebouncedQuery();
  const list = useMaintenanceInstallations({
    page: 1,
    pageSize: 25,
    sortBy: 'name',
    sortOrder: 'asc',
    customerId,
    projectId,
    q: search.q || undefined,
  });
  const selected = useMaintenanceInstallation(value);
  const selectedLabel = selected.data
    ? `${selected.data.installationNumber} – ${selected.data.name}`
    : null;
  const options = useMemo(
    () =>
      mergeSelectedOption(
        (list.data?.items ?? []).map((row) => ({
          value: row.id,
          label: `${row.installationNumber} – ${row.name}`,
        })),
        value,
        selectedLabel,
      ),
    [list.data?.items, selectedLabel, value],
  );

  return (
    <div className="min-w-0 space-y-2">
      <Label htmlFor="filter-installation-q">Anlage suchen</Label>
      <Input
        id="filter-installation-q"
        className="min-h-11"
        placeholder="Kennung oder Bezeichnung"
        value={search.draft}
        disabled={disabled}
        onChange={(e) => search.setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            search.flush();
          }
        }}
      />
      <MaintenanceOptionalSelect
        label="Anlage"
        id="filter-installation"
        value={value}
        onValueChange={onChange}
        items={options}
        disabled={(disabled ?? false) || list.isPending}
      />
    </div>
  );
}
